import { isoWeekLabel } from "./isoWeek.js";
import { shiftPeriodWithYear } from "./shiftPeriod.js";
import type { CorrectionRequest } from "../components/PendingAttendanceCorrectionsQueue.js";
import type { ShiftDayRequestRow } from "../components/ShiftDayRequestQueue.js";
import type { MemberHistoryItem, WeeklyHoursHistoryRow } from "../pages/AttendancePage.js";

/** How many lines the panel shows. */
export const HISTORY_LIMIT = 15;

const decided = (status: string | null | undefined) => status === "APPROVED" || status === "REJECTED";

/**
 * The "Recent Approval History" lines for one employee, from every queue that can
 * decide something about them.
 *
 * One builder rather than one per caller: the panel is loaded from two places
 * (the attendance page and the hook behind the department browser) and they had
 * their own copies, so a kind of request added to one was missing from the other.
 * Shift time changes were decided but never appeared here at all.
 */
export function buildMemberHistory(sources: {
  corrections: CorrectionRequest[];
  weekly: WeeklyHoursHistoryRow[];
  shiftDays: ShiftDayRequestRow[];
}): MemberHistoryItem[] {
  const dailyItems: MemberHistoryItem[] = sources.corrections
    .filter((c) => decided(c.status))
    .map((c) => ({
      key: `daily-${c.id}`,
      label: `Insufficient hours — ${new Date(c.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}`,
      status: c.status,
      approverRole: c.approverRole,
      remark: c.reason,
      sortDate: c.date,
    }));

  const weeklyItems: MemberHistoryItem[] = sources.weekly
    .filter((w) => decided(w.status))
    .map((w) => ({
      key: `weekly-${w.weekStart}`,
      label: `Weekly Hours — ${isoWeekLabel(w.weekStart)}`,
      status: w.status!,
      approverRole: w.approverRole,
      remark: w.approverRemark,
      sortDate: w.weekStart,
    }));

  const shiftItems: MemberHistoryItem[] = sources.shiftDays
    .filter((r) => decided(r.status))
    .map((r) => ({
      key: `shift-${r.id}`,
      // The hours are the whole point of the request, so they belong in the label
      // rather than only in the remark -- a line reading just "Shift Time Change"
      // says nothing about what was agreed.
      label: `Shift Time Change — ${shiftPeriodWithYear(r.startDate, r.endDate)} · ${r.requestedStartHHMM}–${r.requestedEndHHMM}`,
      status: r.status,
      approverRole: r.approverRole ?? null,
      // The approver's words if they left any, otherwise the employee's reason --
      // the same order the other two use.
      remark: r.approverRemark || r.remark,
      sortDate: r.startDate,
    }));

  return [...dailyItems, ...weeklyItems, ...shiftItems]
    .sort((a, b) => b.sortDate.localeCompare(a.sortDate))
    .slice(0, HISTORY_LIMIT);
}
